// GRN phase 1 (2026-10-21): the new receive window (path 2 deliveries, and any open PO).
// g04 delivery note / invoice number or a photo · g06 received on · g10 every rejection gets an outcome ·
// g14 price paid typed here · g15 "Goes to" required when the line has none · the rest: still coming by a date,
// or closed (Ahmed approves). Saved by receiving.service receiveGoods → the existing receipt step (createGRN).
// 2026-10-22: laptop table + rejection window per the mockup.
// 2026-10-25 (rejected goods step 2): a line waiting for a replacement asks whether this delivery is it (mockup "At the door" 2).
// 2026-10-25 (step 4a, Ahmed's rule; mockup DyrAbPJDgtF8t6mjFfytkv "Door & returns" 1): at the door faulty goods are replaced, short-closed
// (closed on the PO line now by whoever saves the receipt, no request to Ahmed) or kept at a discount. No return and no write-off here.
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Drawer, Input, InputNumber, Modal, Radio, Select, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { getOpenReplacements, getPoPayments, OpenReplacement, PoPayments, ProofFile, receiveGoods, ReceiveLinePayload, RejectOutcome } from '../../api';
import CostCenterSelect from '../CostCenterSelect';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import { InvoiceReadings, useInvoiceReader } from '../invoices/InvoicePanel';
import { useAuth } from '../../context/AuthContext';
import { daysBack, isoDay, MR_ACTION_OPTIONS, money, OUTCOME_LABEL, OUTCOME_OPTIONS, OutcomeCard, outsideReceiptWindow, ProofPicker, REASON_AFTER_DAYS, REJECT_REASONS } from './shared';

interface LineState {
  arrived: number | null;
  rejected: number | null;
  reason?: string;
  outcome?: RejectOutcome;
  dueDate: Dayjs | null;
  amount: number | null;
  /** Discount (Ahmed 2026-10-24): paid already → the vendor owes us the amount; not paid yet → we pay it less. */
  alreadyPaid: boolean | null;
  outcomeNote: string;
  /** Short close (step 4a): what the requester gets for the closed qty. */
  shortMr: 'not_needed' | 'reorder';
  price: number | null;
  ccId: number | null;
  restAction?: 'coming' | 'close';
  restDate: Dayjs | null;
  restReason: string;
  mrAction: 'not_needed' | 'reorder';
  /** Door 2: this delivery is the replacement waiting on the line (ticked by default when one is waiting). */
  replaces: boolean;
}

/** What the rejection window edits; written to the line only on Save. */
type RejectDraft = Pick<LineState, 'reason' | 'outcome' | 'dueDate' | 'amount' | 'alreadyPaid' | 'outcomeNote' | 'shortMr'>;

interface Props {
  open: boolean;
  /** The PO as returned by getPO (lines with qty, totalReceived, cancelledQty, prices, unit, costCenter). */
  po: any;
  onClose: () => void;
  onSaved: (grn: any) => void;
}

const MUTED = '#5f6672';
const n = (v: unknown) => Number(v ?? 0);
const round2 = (x: number) => Math.round(x * 100) / 100;
const openOf = (l: any) => round2(n(l.qty) - n(l.totalReceived) - n(l.cancelledQty));
const lab: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: MUTED, minWidth: 0 };
const fieldLab: React.CSSProperties = { fontWeight: 600, display: 'block', marginBottom: 6 };
const cardText: React.CSSProperties = { fontSize: 12.5, color: MUTED };
const top = () => ({ style: { verticalAlign: 'top' as const } });

/** Under Rejected: "Vendor replaces it · due 27 Oct", "Keep at a discount · vendor owes 40.00" / "· pay 40.00 less", ... */
const outcomeSummary = (s: LineState) => {
  const label = OUTCOME_LABEL[s.outcome || ''] || '';
  if (s.outcome === 'replacement' && s.dueDate) return `${label} · due ${s.dueDate.format('D MMM')}`;
  if (s.outcome === 'discount' && s.amount) return `${label} · ${s.alreadyPaid === false ? `pay ${money(s.amount)} less` : `vendor owes ${money(s.amount)}`}`;
  if (s.outcome === 'short_close') return `${label} · closes on the PO now`;
  return label;
};

const ReceiveWindow: React.FC<Props> = ({ open, po, onClose, onSaved }) => {
  const compact = useCompactLayout();
  const openLines = useMemo(() => (po?.lines || []).filter((l: any) => openOf(l) > 0 && l.cancelStatus !== 'Pending'), [po]);
  const lockedLines = useMemo(() => (po?.lines || []).filter((l: any) => openOf(l) > 0 && l.cancelStatus === 'Pending'), [po]);
  const [receivedOn, setReceivedOn] = useState<Dayjs | null>(dayjs());
  const [dateReason, setDateReason] = useState('');
  const [docRef, setDocRef] = useState('');
  const [proof, setProof] = useState<ProofFile[]>([]);
  // Step 4d (M4, mockup "Invoice reading" 1, Ahmed's I2): every photo added here is read; an invoice comes back to confirm.
  const { canAccess } = useAuth();
  const canReadInvoices = canAccess('purchasing.account', 'create');
  const invoiceReader = useInvoiceReader(open && canReadInvoices ? po?.poId : null, proof, 'receipt');
  const [notes, setNotes] = useState('');
  const [st, setSt] = useState<Record<number, LineState>>({});
  const [saving, setSaving] = useState(false);
  // The rejection window: which line, whether it already had an outcome, and the unsaved answers.
  const [rej, setRej] = useState<{ id: number; had: boolean; d: RejectDraft } | null>(null);
  // Door 2: rejections on this PO still waiting for their replacement (one per line is offered: the oldest).
  const [repl, setRepl] = useState<OpenReplacement[]>([]);
  useEffect(() => {
    if (!open || !po?.poId) { setRepl([]); return; }
    getOpenReplacements(po.poId).then(setRepl).catch(() => setRepl([]));
  }, [open, po?.poId]);
  const replFor = (lineId: number) => repl.find((r) => r.poLineId === lineId) || null;
  // Short close (mockup "Door & returns" 1): what was paid on the line, so the window can say what the vendor will owe us.
  const [paid, setPaid] = useState<PoPayments | null>(null);
  useEffect(() => {
    if (!open || !po?.poId) { setPaid(null); return; }
    getPoPayments(po.poId).then(setPaid).catch(() => setPaid(null)); // no access to payments: the note is left out
  }, [open, po?.poId]);

  useEffect(() => {
    if (!open || !po) return;
    setReceivedOn(dayjs()); setDateReason(''); setDocRef(''); setProof([]); setNotes(''); setRej(null);
    const expected = po.expectedDelivery ? dayjs(po.expectedDelivery) : null;
    setSt(Object.fromEntries(openLines.map((l: any) => [l.id, {
      arrived: openOf(l), rejected: 0, dueDate: null, amount: null, alreadyPaid: null, outcomeNote: '', shortMr: 'not_needed',
      price: l.actualUnitPrice != null ? n(l.actualUnitPrice) : n(l.unitPrice) > 0 ? n(l.unitPrice) : null,
      ccId: null, restDate: l.nextExpected ? dayjs(l.nextExpected) : expected && expected.isAfter(dayjs(), 'day') ? expected : dayjs().add(7, 'day'),
      restReason: '', mrAction: 'not_needed', replaces: true,
    } as LineState])));
  }, [open, po]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (id: number, patch: Partial<LineState>) => setSt((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const askReject = (id: number) => {
    const s = st[id];
    if (!s) return;
    setRej((cur) => (cur && cur.id === id ? cur : {
      id, had: !!s.outcome, d: { reason: s.reason, outcome: s.outcome, dueDate: s.dueDate, amount: s.amount, alreadyPaid: s.alreadyPaid, outcomeNote: s.outcomeNote, shortMr: s.shortMr },
    }));
  };
  // Rejected: a new rejection opens its window when the field is left or Enter is pressed, not on every keystroke.
  const rejectedDone = (id: number, typed: string) => {
    if (Number(typed) > 0 && !st[id]?.outcome) askReject(id);
  };
  const editRej = (patch: Partial<RejectDraft>) => setRej((r) => (r ? { ...r, d: { ...r.d, ...patch } } : r));
  const saveReject = () => { if (rej) set(rej.id, rej.d); setRej(null); };
  // Cancel drops the window's edits; a rejection that never had an outcome goes back to 0.
  const cancelReject = () => { if (rej && !rej.had) set(rej.id, { rejected: 0 }); setRej(null); };

  // What the window will save for each line, and what is still missing.
  const plan = useMemo(() => openLines.map((l: any) => {
    const s = st[l.id];
    if (!s) return null;
    const open = openOf(l);
    const arrived = s.arrived || 0;
    const rejected = s.rejected || 0;
    const accepted = round2(s.outcome === 'discount' ? arrived : arrived - rejected);
    const remaining = round2(open - accepted);
    // A replacement keeps the rejected qty open until it comes; a short close closes it now (step 4a). "The rest" is what did not arrive.
    const replQty = s.outcome === 'replacement' ? Math.min(rejected, Math.max(0, remaining)) : 0;
    const shortQty = s.outcome === 'short_close' ? Math.min(rejected, Math.max(0, remaining)) : 0;
    const rest = Math.max(0, round2(remaining - replQty - shortQty));
    const hasCc = !!(l.costCenterId || l.costCenter);
    const needsPrice = l.actualUnitPrice == null;
    const issues: string[] = [];
    const tag = `Line ${l.lineNo}`;
    if (arrived > 0) {
      if (rejected < 0 || rejected > arrived) issues.push(`${tag}: rejected must be between 0 and what arrived.`);
      if (accepted > open + 1e-9) issues.push(`${tag}: ${accepted} accepted is more than the ${open} still open.`);
      if (rejected > 0) {
        if (!s.reason) issues.push(`${tag}: say why ${rejected} were rejected.`);
        if (!s.outcome) issues.push(`${tag}: choose what happens to the rejected goods.`);
        if (s.outcome === 'replacement' && !s.dueDate) issues.push(`${tag}: when is the replacement due?`);
        if (s.outcome === 'discount' && !(s.amount && s.amount > 0)) issues.push(`${tag}: enter the discount amount.`);
        if (s.outcome === 'discount' && s.alreadyPaid == null) issues.push(`${tag}: has the vendor been paid for these? Choose paid already or not paid yet.`);
      }
      if (!(s.price && s.price > 0)) issues.push(`${tag}: enter the price paid.`);
      if (!hasCc && !s.ccId) issues.push(`${tag}: choose where it goes (job or store).`);
      if (rest > 0) {
        if (!s.restAction) issues.push(`${tag}: ${rest} is still open. Choose "still coming" or "close the rest".`);
        if (s.restAction === 'coming' && !s.restDate) issues.push(`${tag}: when is the rest expected?`);
        if (s.restAction === 'close' && !s.restReason.trim()) issues.push(`${tag}: say why the rest is closed.`);
      }
    }
    return { l, s, open, arrived, rejected, accepted, rest, hasCc, needsPrice, issues, value: accepted * (s.price || 0) };
  }).filter(Boolean) as any[], [openLines, st]);

  const back = daysBack(receivedOn);
  const arriving = plan.filter((p) => p.arrived > 0);
  const problems = [
    ...(!receivedOn ? ['Choose the date the goods arrived.'] : []),
    ...(back > REASON_AFTER_DAYS && !dateReason.trim() ? [`Received ${back} days ago: give a reason.`] : []),
    ...(!docRef.trim() && !proof.length ? ['Give the delivery note or invoice number, or add a photo.'] : []),
    ...(!arriving.length ? ['Enter what arrived on at least one line.'] : []),
    ...arriving.flatMap((p) => p.issues),
  ];
  const total = arriving.reduce((s, p) => s + p.value, 0);
  // Arriving now, per unit: the accepted quantities of the same lines the money total counts.
  const perUnit = Object.entries(arriving.reduce<Record<string, number>>((m, p) => {
    const u = p.l.unit?.name || '';
    m[u] = round2((m[u] || 0) + p.accepted);
    return m;
  }, {})).filter(([, q]) => q > 0);

  const save = async () => {
    if (problems.length) { message.warning(problems[0]); return; }
    setSaving(true);
    try {
      const lines: ReceiveLinePayload[] = arriving.map((p) => ({
        poLineId: p.l.id,
        arrivedQty: p.arrived,
        rejectedQty: p.rejected || undefined,
        rejectReason: p.rejected > 0 ? p.s.reason : undefined,
        actualUnitPrice: p.s.price,
        costCenterId: p.hasCc ? undefined : p.s.ccId,
        outcome: p.rejected > 0 && p.s.outcome ? {
          type: p.s.outcome,
          dueDate: p.s.outcome === 'replacement' ? isoDay(p.s.dueDate) : undefined,
          amount: p.s.outcome === 'discount' ? p.s.amount : undefined,
          alreadyPaid: p.s.outcome === 'discount' ? p.s.alreadyPaid ?? undefined : undefined,
          note: p.s.outcomeNote.trim() || undefined,
          mrAction: p.s.outcome === 'short_close' ? p.s.shortMr : undefined,
        } : undefined,
        rest: p.rest > 0 && p.s.restAction ? {
          action: p.s.restAction,
          date: p.s.restAction === 'coming' ? isoDay(p.s.restDate) : undefined,
          reason: p.s.restAction === 'close' ? p.s.restReason.trim() : undefined,
          mrAction: p.s.mrAction,
        } : undefined,
        // Door 2: asked whenever a replacement waits on the line; linked when ticked and something was accepted.
        replacementAsked: replFor(p.l.id) ? true : undefined,
        replacesGrnLineId: replFor(p.l.id) && p.s.replaces && p.accepted > 0 ? replFor(p.l.id)!.grnLineId : undefined,
      }));
      const res = await receiveGoods(po.poId, {
        receivedOn: isoDay(receivedOn),
        receivedOnReason: dateReason.trim() || undefined,
        deliveryNoteRef: docRef.trim() || undefined,
        proofFiles: proof,
        notes: notes.trim() || undefined,
        lines,
      });
      message.success(`${res.grn.grnId} saved`);
      res.warnings?.forEach((w) => message.warning(w, 8));
      onSaved(res.grn);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Could not save the receipt', 8);
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: compact ? 16 : '12px 16px' }}>
      <div>
        <label style={fieldLab} htmlFor="rw-date"><span style={{ color: '#cf1322' }}>*</span> Received on</label>
        <DatePicker id="rw-date" value={receivedOn} onChange={setReceivedOn} format="D MMM YYYY" allowClear={false} disabledDate={outsideReceiptWindow} style={{ width: '100%' }} inputReadOnly={compact} />
        {back > REASON_AFTER_DAYS && <Input style={{ marginTop: 6 }} placeholder={`Why ${back} days later?`} value={dateReason} onChange={(e) => setDateReason(e.target.value)} maxLength={300} />}
      </div>
      <div>
        <label style={fieldLab} htmlFor="rw-doc"><span style={{ color: '#cf1322' }}>*</span> Delivery note or invoice</label>
        {/* Laptop: the number and the photo on one row, as in the mockup. */}
        <div style={compact ? { display: 'flex', flexDirection: 'column', gap: 6 } : { display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <Input id="rw-doc" placeholder="Number, e.g. DN-7781" value={docRef} onChange={(e) => setDocRef(e.target.value)} maxLength={100} style={compact ? undefined : { flex: 1, minWidth: 0 }} />
          <div style={compact ? undefined : { flex: 'none', maxWidth: 260 }}><ProofPicker value={proof} onChange={setProof} label="Or add a photo" /></div>
        </div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>A number or a photo is enough.</div>
      </div>
      {invoiceReader.items.length > 0 && <div style={compact ? undefined : { gridColumn: '1 / -1' }}><InvoiceReadings reader={invoiceReader} canAct={canReadInvoices} /></div>}
      <div style={compact ? undefined : { gridColumn: '1 / -1' }}>
        <label style={fieldLab} htmlFor="rw-notes">Notes</label>
        <Input.TextArea id="rw-notes" rows={compact ? 2 : 1} autoSize={compact ? undefined : { minRows: 1, maxRows: 4 }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
      </div>
    </div>
  );

  // Under Rejected (both layouts): the saved outcome, or a red prompt while none is chosen. Opens the rejection window.
  const rejectLink = (p: any) => p.rejected > 0 && (
    <Button type="link" size="small" danger={!p.s.outcome} onClick={() => askReject(p.l.id)} style={{ display: 'block', padding: 0, height: 'auto', fontSize: 12, whiteSpace: 'normal', textAlign: 'inherit', marginTop: 2 }}>
      {p.s.outcome ? outcomeSummary(p.s) : 'Choose what happens'}
    </Button>
  );

  // Laptop: one row per line, the columns of the mockup.
  // Door 2 (mockup "At the door" 2): "Replacement for the rejection on GRN-000771 (10 pcs, due 29 Oct) · This delivery replaces it".
  const replBox = (p: any) => {
    const r = replFor(p.l.id);
    if (!r) return null;
    const unit = p.l.unit?.name || '';
    return (
      <div style={{ marginTop: compact ? 8 : 0, padding: '6px 10px', border: '1px solid #91caff', background: '#e6f4ff', borderRadius: 6, fontSize: 12.5 }}>
        {!compact && <span style={{ color: MUTED }}>Line {p.l.lineNo} · </span>}
        <b>Replacement for the rejection on {r.grnId}</b> ({r.remaining}{unit ? ` ${unit}` : ''}{r.due ? `, due ${dayjs(r.due).format('D MMM')}` : ''})
        <div style={{ marginTop: 4 }}>
          <Checkbox checked={p.s.replaces} onChange={(e) => set(p.l.id, { replaces: e.target.checked })}>
            This delivery replaces it · <bdi>{po?.vendor?.name || 'the vendor'}</bdi> took the faulty ones back
          </Checkbox>
        </div>
      </div>
    );
  };

  const columns: ColumnsType<any> = [
    { title: '#', key: 'no', width: 36, onCell: top, render: (_, p) => p.l.lineNo },
    {
      title: 'Item', key: 'item', onCell: top,
      render: (_, p) => (p.l.partNumber
        ? <><code>{p.l.partNumber}</code><div dir="auto" style={{ fontSize: 12, color: MUTED }}>{p.l.itemDescription}</div></>
        : <div dir="auto">{p.l.itemDescription}</div>),
    },
    { title: 'Open', key: 'open', width: 76, align: 'right', onCell: top, render: (_, p) => <span style={{ whiteSpace: 'nowrap' }}>{p.open} {p.l.unit?.name || ''}</span> },
    {
      title: 'Arrived', key: 'arrived', width: 84, align: 'right', onCell: top,
      render: (_, p) => <InputNumber size="small" min={0} value={p.s.arrived} onChange={(v) => set(p.l.id, { arrived: v == null ? null : Number(v) })} inputMode="decimal" style={{ width: '100%' }} aria-label={`Line ${p.l.lineNo} arrived`} />,
    },
    {
      title: 'Rejected', key: 'rejected', width: 120, align: 'right', onCell: top,
      render: (_, p) => (
        <>
          <InputNumber
            size="small" min={0} value={p.s.rejected} onChange={(v) => set(p.l.id, { rejected: v == null ? null : Number(v) })}
            onBlur={(e) => rejectedDone(p.l.id, e.target.value)} onPressEnter={(e) => rejectedDone(p.l.id, e.currentTarget.value)}
            inputMode="decimal" style={{ width: '100%' }} aria-label={`Line ${p.l.lineNo} rejected`}
          />
          {rejectLink(p)}
        </>
      ),
    },
    {
      title: 'Price paid', key: 'price', width: 104, align: 'right', onCell: top,
      render: (_, p) => (
        <>
          <InputNumber size="small" min={0} precision={2} value={p.s.price} status={p.s.price ? undefined : 'error'} onChange={(v) => set(p.l.id, { price: v == null ? null : Number(v) })} inputMode="decimal" style={{ width: '100%' }} aria-label={`Line ${p.l.lineNo} price paid, each`} />
          {p.needsPrice && <div style={{ fontSize: 12, color: MUTED }}>typed here</div>}
        </>
      ),
    },
    {
      title: 'Goes to', key: 'cc', width: 190, onCell: top,
      render: (_, p) => (p.hasCc
        ? <span style={{ fontSize: 13 }}><code>{p.l.costCenter?.code}</code> {p.l.costCenter?.name || ''}</span>
        : (
          <>
            <CostCenterSelect size="small" value={p.s.ccId} onChange={(id) => set(p.l.id, { ccId: id })} placeholder="Choose job or store" status={p.s.ccId ? undefined : 'error'} style={{ width: '100%' }} />
            <div style={{ fontSize: 12, color: p.s.ccId ? MUTED : '#cf1322', marginTop: 2 }}>required: the MR line has none</div>
            <div style={{ fontSize: 12, color: MUTED }}>your choice becomes the MR line's cost center</div>
          </>
        )),
    },
    {
      title: 'The rest', key: 'rest', width: 270, onCell: top,
      render: (_, p) => {
        const { l, s } = p;
        if (!(p.arrived > 0)) return <span style={{ fontSize: 12, color: MUTED }}>stays open</span>;
        if (!(p.rest > 0)) return <span style={{ color: MUTED }}>complete</span>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Radio.Group value={s.restAction} onChange={(e) => set(l.id, { restAction: e.target.value })} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 4 }}>
                <Radio value="coming" style={{ marginInlineEnd: 4 }}>{p.rest} {l.unit?.name || ''} still coming by</Radio>
                {/* Picking a date means "still coming". */}
                <DatePicker size="small" value={s.restDate} onChange={(d) => set(l.id, { restDate: d, restAction: 'coming' })} format="D MMM" disabledDate={(d) => d.isBefore(receivedOn || dayjs(), 'day')} style={{ width: 96 }} aria-label={`Line ${l.lineNo}: rest expected by`} />
              </div>
              <Radio value="close">Close the rest <span style={{ color: MUTED }}>(asks Ahmed)</span></Radio>
            </Radio.Group>
            {s.restAction === 'close' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Input size="small" value={s.restReason} onChange={(e) => set(l.id, { restReason: e.target.value })} placeholder="Why close the rest? (required)" maxLength={300} />
                <Select size="small" value={s.mrAction} onChange={(v) => set(l.id, { mrAction: v })} options={MR_ACTION_OPTIONS} />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const unitSum = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      <span style={{ fontSize: 12.5, color: MUTED }}>Arriving now:</span>
      {perUnit.length
        ? perUnit.map(([u, q]) => (
          <span key={u} style={{ border: '1px solid #e8e8e8', borderRadius: 999, padding: '1px 10px', fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}><b style={{ fontWeight: 600 }}>{q}</b>{u ? ` ${u}` : ''}</span>
        ))
        : <span style={{ fontSize: 12.5, color: MUTED }}>nothing yet</span>}
      <span style={{ marginLeft: 'auto', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>EGP {money(total)}</span>
    </div>
  );

  // Phone: one block per line.
  const lineBlock = (p: any, i: number) => {
    const { l, s } = p;
    const unit = l.unit?.name || '';
    return (
      <div key={l.id} style={{ padding: 12, borderTop: i ? '1px solid #f0f0f0' : undefined, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
          <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: 12, color: MUTED }}>Line {l.lineNo}{l.mrLine?.mrHeader?.mrId ? ` · ${l.mrLine.mrHeader.mrId}` : ''}</span>
            <div>{l.partNumber ? <code style={{ marginRight: 6 }}>{l.partNumber}</code> : null}{l.itemDescription}</div>
          </div>
          <span style={{ whiteSpace: 'nowrap', fontSize: 13 }}>Open <b>{p.open}</b> {unit}</span>
        </div>
        {replBox(p)}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, alignItems: 'start' }}>
          <label style={lab}>Arrived
            <InputNumber min={0} value={s.arrived} onChange={(v) => set(l.id, { arrived: v == null ? null : Number(v) })} inputMode="decimal" style={{ width: '100%' }} />
          </label>
          <div style={lab}>
            <label style={lab}>Rejected
              <InputNumber
                min={0} value={s.rejected} onChange={(v) => set(l.id, { rejected: v == null ? null : Number(v) })}
                onBlur={(e) => rejectedDone(l.id, e.target.value)} onPressEnter={(e) => rejectedDone(l.id, e.currentTarget.value)}
                inputMode="decimal" style={{ width: '100%' }}
              />
            </label>
            {rejectLink(p)}
          </div>
          <label style={lab}>Price paid, each{p.needsPrice ? ' *' : ''}
            <InputNumber min={0} precision={2} value={s.price} status={s.price ? undefined : 'error'} onChange={(v) => set(l.id, { price: v == null ? null : Number(v) })} inputMode="decimal" style={{ width: '100%' }} />
          </label>
          <div style={{ ...lab, gridColumn: '1 / -1' }}>Goes to
            {p.hasCc
              ? <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.88)' }}><code>{l.costCenter?.code}</code> {l.costCenter?.name || ''}</span>
              : <CostCenterSelect value={s.ccId} onChange={(id) => set(l.id, { ccId: id })} placeholder="Choose job or store (required)" status={s.ccId ? undefined : 'error'} />}
          </div>
        </div>

        {p.arrived > 0 && p.rest > 0 && (
          <div style={{ border: '1px solid #ffe58f', background: '#fffbe6', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <b style={{ fontSize: 13 }}>The rest: {p.rest} {unit} still open</b>
            <Radio.Group value={s.restAction} onChange={(e) => set(l.id, { restAction: e.target.value })} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Radio value="coming">Still coming</Radio>
              <Radio value="close">Close the rest <span style={{ fontSize: 12, color: MUTED }}>· asks Ahmed</span></Radio>
            </Radio.Group>
            {s.restAction === 'coming' && (
              <label style={lab}>Expected by
                <DatePicker value={s.restDate} onChange={(d) => set(l.id, { restDate: d })} format="D MMM YYYY" disabledDate={(d) => d.isBefore(receivedOn || dayjs(), 'day')} style={{ maxWidth: 220 }} inputReadOnly />
              </label>
            )}
            {s.restAction === 'close' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                <Input value={s.restReason} onChange={(e) => set(l.id, { restReason: e.target.value })} placeholder="Why close the rest? (required)" maxLength={300} />
                <Select value={s.mrAction} onChange={(v) => set(l.id, { mrAction: v })} options={MR_ACTION_OPTIONS} />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // The rejection window (laptop and phone): why, what happens next, a note. Same rules as the plan's issues.
  const rl = rej ? openLines.find((l: any) => l.id === rej.id) : null;
  const rUnit = rl?.unit?.name || '';
  const rQty = rej ? n(st[rej.id]?.rejected) : 0;
  const draft = rej?.d;
  const rejOk = !!draft && !!draft.reason && !!draft.outcome
    && (draft.outcome !== 'replacement' || !!draft.dueDate)
    && (draft.outcome !== 'discount' || (!!(draft.amount && draft.amount > 0) && draft.alreadyPaid != null));
  // Short close after paying: the line is then worth less than was paid on it, and the vendor owes us the difference
  // (the same sum the server makes: paid − the value kept on the line). Shown only to people who can see payments.
  const paidLine = rl && paid ? paid.lines.find((x) => x.id === rl.id) : null;
  const shortWorth = rl ? round2(Math.max(0, n(rl.qty) - n(rl.cancelledQty) - Math.min(rQty, openOf(rl))) * n(st[rl.id]?.price ?? rl.actualUnitPrice ?? rl.unitPrice)) : 0;
  // Less what the vendor already settled for earlier short closes on this line (the server's shortCloseOwed does the same; code review 25 Oct).
  const shortSettled = rl && paid ? paid.credits.filter((c) => c.kind === 'short_close' && c.poLineId === rl.id).reduce((t, c) => t + (c.settlement?.amount || 0), 0) : 0;
  const shortOwes = paidLine && paidLine.paid > 0 ? round2(Math.max(0, paidLine.paid - shortWorth - shortSettled)) : 0;
  const paidCodes = rl && paid ? paid.payments.filter((x) => x.status !== 'superseded' && x.lines.some((y) => y.poLineId === rl.id)).map((x) => x.code) : [];

  const outcomeCard = (o: (typeof OUTCOME_OPTIONS)[number]) => {
    const on = draft?.outcome === o.value;
    return (
      <OutcomeCard key={o.value} label={o.label} on={on} onPick={() => editRej({ outcome: o.value })}>
        {/* Rejected goods mockup (2026-10-24, "At the door" 1): card wording; D3 — a discount is money the vendor owes us. */}
        {o.value === 'replacement' && (
          <div style={cardText}>
            {o.hint}
            {on && <> Due <DatePicker size="small" value={draft?.dueDate} onChange={(v) => editRej({ dueDate: v })} format="D MMM YYYY" disabledDate={(v) => v.isBefore(receivedOn || dayjs(), 'day')} style={{ width: 130 }} inputReadOnly={compact} aria-label="Replacement due" />.</>}
          </div>
        )}
        {o.value === 'discount' && (() => {
          // Ahmed (2026-10-24): already paid → the vendor owes us the amount; not paid yet → we pay it less. The line keeps
          // the price paid either way, so the item's price history stays true.
          const vendor = po?.vendor?.name || 'the vendor';
          const amt = on
            ? <InputNumber size="small" min={0} precision={2} value={draft?.amount} onChange={(v) => editRej({ amount: v == null ? null : Number(v) })} inputMode="decimal" style={{ width: 100 }} aria-label="Discount (EGP)" />
            : null;
          return (
            <div style={{ ...cardText, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {!on && <span>{o.hint}</span>}
              {on && draft?.alreadyPaid == null && <span>We keep the goods at the price paid. The discount: {amt} EGP.</span>}
              {on && draft?.alreadyPaid === true && <span>We keep the goods at the price paid. <b><bdi>{vendor}</bdi> owes us</b> {amt} EGP, by credit note, refund or deduction on another invoice.</span>}
              {on && draft?.alreadyPaid === false && <span>We keep the goods at the price paid. <b>We pay</b> {amt} EGP <b>less</b> when we pay <bdi>{vendor}</bdi>.</span>}
              {on && (
                <div onClick={(e) => e.stopPropagation()}>
                  <div id="rw-paid" style={{ fontWeight: 600, color: 'rgba(0,0,0,0.88)', marginBottom: 4 }}><span style={{ color: '#cf1322' }}>*</span> Has <bdi>{vendor}</bdi> been paid for these?</div>
                  <Radio.Group aria-labelledby="rw-paid" value={draft?.alreadyPaid ?? undefined} onChange={(e) => editRej({ alreadyPaid: e.target.value })} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Radio value={true}>Paid already: the vendor owes us the discount</Radio>
                    <Radio value={false}>Not paid yet: we pay the discount less</Radio>
                  </Radio.Group>
                </div>
              )}
            </div>
          );
        })()}
        {o.value === 'short_close' && (
          <div style={{ ...cardText, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {on
              ? <span><bdi>{po?.vendor?.name || 'The vendor'}</bdi> will not replace it. <b>{Math.min(rQty, rl ? openOf(rl) : rQty)}{rUnit ? ` ${rUnit}` : ''} close on the PO line</b> now, logged in the PO history.</span>
              : <span>{o.hint}</span>}
            {on && (
              <div onClick={(e) => e.stopPropagation()}>
                <div id="rw-short-mr" style={{ fontWeight: 600, color: 'rgba(0,0,0,0.88)', marginBottom: 2 }}>For the requester</div>
                <Radio.Group aria-labelledby="rw-short-mr" value={draft?.shortMr} onChange={(e) => editRej({ shortMr: e.target.value })} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Radio value="not_needed">No longer needed</Radio>
                  <Radio value="reorder">Order it again</Radio>
                </Radio.Group>
              </div>
            )}
          </div>
        )}
      </OutcomeCard>
    );
  };

  // Phones: antd caps a modal at the screen width less 16 px, so 520 fits a 375 px screen without sideways scroll.
  const rejectWindow = (
    <Modal
      open={!!rej}
      title={rl ? <div style={{ paddingInlineEnd: 28 }}>Line {rl.lineNo} · {rQty}{rUnit ? ` ${rUnit}` : ''} rejected · <bdi>{rl.itemDescription}</bdi> · <bdi>{po?.vendor?.name || ''}</bdi></div> : null}
      onCancel={cancelReject}
      onOk={saveReject}
      okText="Save"
      okButtonProps={{ disabled: !rejOk }}
      maskClosable={false}
      width={compact ? 520 : 640}
    >
      {draft && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div id="rw-rej-why" style={fieldLab}><span style={{ color: '#cf1322' }}>*</span> Why</div>
            <Radio.Group aria-labelledby="rw-rej-why" optionType="button" options={REJECT_REASONS} value={draft.reason} onChange={(e) => editRej({ reason: e.target.value })} style={compact ? { display: 'flex', flexWrap: 'wrap', rowGap: 6 } : undefined} />
          </div>
          <div>
            <div id="rw-rej-next" style={fieldLab}><span style={{ color: '#cf1322' }}>*</span> What happens next</div>
            {/* Mockup "Door & returns" 1: the three door choices side by side on a laptop. */}
            <div role="radiogroup" aria-labelledby="rw-rej-next" style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
              {OUTCOME_OPTIONS.map(outcomeCard)}
            </div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>Goods we accept and later find faulty are returned from the receipt page or Returns &amp; credits.</div>
          </div>
          {draft.outcome === 'short_close' && shortOwes > 0 && (
            <Alert
              type="info" showIcon
              message={<span><bdi>{po?.vendor?.name || 'The vendor'}</bdi> was paid <b>{money(paidLine!.paid)}</b> for this line{paidCodes.length ? ` (${paidCodes.join(', ')})` : ''}. After the short close the line is worth {money(shortWorth)}{shortSettled > 0 ? ` (and ${money(shortSettled)} came back already)` : ''}: <b><bdi>{po?.vendor?.name || 'the vendor'}</bdi> owes us {money(shortOwes)}</b> on this line.</span>}
            />
          )}
          <div>
            <label htmlFor="rw-rej-note" style={fieldLab}>Note</label>
            <Input id="rw-rej-note" value={draft.outcomeNote} onChange={(e) => editRej({ outcomeNote: e.target.value })} placeholder="Optional" maxLength={300} />
          </div>
        </div>
      )}
    </Modal>
  );

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {header}
      {openLines.length === 0 ? (
        <div style={{ color: MUTED }}>Nothing is open on this PO.</div>
      ) : compact ? (
        <div style={{ border: '1px solid #f0f0f0', borderRadius: 10, overflow: 'hidden' }}>{plan.map(lineBlock)}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Table
            size="small" pagination={false} rowKey={(p: any) => p.l.id} columns={columns} dataSource={plan} scroll={{ x: 1040 }}
            // Door 2: the replacement question sits under its line, the full width of the table.
            expandable={{
              expandedRowRender: (p: any) => replBox(p),
              rowExpandable: (p: any) => !!replFor(p.l.id),
              expandedRowKeys: plan.filter((p: any) => replFor(p.l.id)).map((p: any) => p.l.id),
              showExpandColumn: false,
            }}
          />
          {unitSum}
        </div>
      )}
      {lockedLines.length > 0 && (
        <div style={{ fontSize: 12, color: MUTED }}>
          Waiting for Ahmed's decision, so not receivable now: {lockedLines.map((l: any) => `line ${l.lineNo}`).join(', ')}.
        </div>
      )}
      <div style={{ fontSize: 12, color: MUTED }}>Set Arrived to 0 for a line that did not come.</div>
      {rejectWindow}
    </div>
  );

  const footer = compact ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <div style={{ fontSize: 12, color: MUTED }}>Arriving now: {arriving.length} line{arriving.length === 1 ? '' : 's'}</div>
          <div style={{ fontSize: 17, fontWeight: 600 }}>{money(total)} EGP</div>
        </div>
        <Button type="primary" loading={saving} disabled={problems.length > 0} onClick={save} style={{ height: 44 }}>Save receipt</Button>
      </div>
      {problems.length > 0 && <div style={{ fontSize: 12, color: '#cf1322', textAlign: 'left' }}>{problems[0]}</div>}
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ marginRight: 'auto', minWidth: 0, textAlign: 'left', fontSize: 12.5, color: '#cf1322' }}>{problems[0] || ''}</span>
      <Button onClick={onClose}>Cancel</Button>
      <Button type="primary" loading={saving} disabled={problems.length > 0} onClick={save}>Save receipt</Button>
    </div>
  );
  const title = `Receive goods · ${po?.poId || ''} · ${po?.vendor?.name || ''}`;

  if (compact) {
    return <Drawer open={open} onClose={onClose} placement="right" width="100%" title={title} footer={footer} destroyOnClose>{content}</Drawer>;
  }
  return <Modal open={open} onCancel={onClose} title={title} footer={footer} width={1120} destroyOnClose maskClosable={false}>{content}</Modal>;
};

export default ReceiveWindow;
